import {
  VALID,
  isBlank,
  hcpcsOrUnitOfMeasureCrossFieldErrors,
  isValidQuantity,
  isQuestionableQuantity,
  isValidIdentifier,
  isValidId340b,
  isValidHealthPlanId,
  isValidHealthPlanName,
  isValidClaimNumber,
  isValidClaimLineNumber,
  isValidHCPCS,
  isValidHCPCSMod,
  isValidUnitOfMeasure,
  isValidWholesalerInvoiceNumber,
  isValidPlaceOfService,
  isValidDiagnosisCode,
  isValidNonFutureDate,
} from "./DataValidations";
import { isRequiredField } from "../Schemas/MedicalEncounterClaim";
import SubmissionTranslator from "../../../utils/SubmissionTranslator";

const translator = new SubmissionTranslator("medical_encounter");

const isValidMedicalEncounterClaimField = (value, column, row, mapping) => {
  if (["formatted_claim_number"].includes(column)) {
    return VALID;
  }

  const requiredResult = checkRequired(column, value);
  if (!requiredResult.isValid) {
    return requiredResult;
  }

  switch (column) {
    case "date_of_service":
    case "encounter_date_of_service":
      if (!value) return VALID;
      if (!isValidNonFutureDate(value)) {
        return { isValid: false, message: translator.getError("invalid_date") };
      }
      return VALID;

    case "ndc":
      return VALID;

    case "quantity":
      if (!isValidQuantity(value)) {
        return { isValid: false, message: translator.getError("invalid_quantity") };
      }
      if (isQuestionableQuantity(value)) {
        return { isValid: false, message: translator.getError("quantity_over_max") };
      }
      return VALID;

    case "encounter_billing_service_provider_id":
    case "encounter_rendering_physician_id":
    case "rendering_physician_id":
      if (!value) return VALID;
      if (!isValidIdentifier(value)) {
        return { isValid: false, message: translator.getError("invalid_identifier") };
      }
      return VALID;

    case "service_provider_id":
      if (!isValidIdentifier(value)) {
        return { isValid: false, message: translator.getError("invalid_identifier") };
      }
      return VALID;

    case "id_340b_raw":
      if (!isValidId340b(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_id_340b"),
        };
      }
      return VALID;

    case "health_plan_id":
      if (!isValidHealthPlanId(value)) {
        return { isValid: false, message: translator.getError("invalid_health_plan_id") };
      }
      return VALID;

    case "health_plan_name":
      if (!isValidHealthPlanName(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_health_plan_name"),
        };
      }
      return VALID;

    case "claim_number":
      if (!isValidClaimNumber(value)) {
        return { isValid: false, message: translator.getError("invalid_claim_number") };
      }
      return VALID;

    case "claim_line_number":
      if (!isValidClaimLineNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_claim_line_number"),
        };
      }
      return VALID;

    case "hcpcs_code":
    case "encounter_hcpcs_code":
      if (!isValidHCPCS(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_encounter_hcpcs_code"),
        };
      }
      return VALID;

    case "hcpcs_code_modifier_1":
    case "hcpcs_code_modifier_2":
    case "hcpcs_code_modifier_3":
    case "hcpcs_code_modifier_4":
    case "encounter_hcpcs_code_modifier_1":
    case "encounter_hcpcs_code_modifier_2":
    case "encounter_hcpcs_code_modifier_3":
    case "encounter_hcpcs_code_modifier_4":
      if (!isValidHCPCSMod(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_2") };
      }
      return VALID;

    case "unit_of_measure":
      if (!isValidUnitOfMeasure(value)) {
        return { isValid: false, message: translator.getError("invalid_uom") };
      }
      return VALID;

    case "encounter_place_of_service_code":
      if (!value) return VALID;
      if (!isValidPlaceOfService(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_place_of_service_code"),
        };
      }
      return VALID;

    case "encounter_diagnosis_code_primary":
    case "encounter_diagnosis_code_2":
    case "encounter_diagnosis_code_3":
    case "encounter_diagnosis_code_4":
    case "encounter_diagnosis_code_5":
    case "encounter_diagnosis_code_6":
    case "encounter_diagnosis_code_7":
    case "encounter_diagnosis_code_8":
    case "encounter_diagnosis_code_9":
    case "encounter_diagnosis_code_10":
      if (!isValidDiagnosisCode(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_diagnosis_code"),
        };
      }
      return VALID;

    case "wholesaler_invoice_number":
      if (!isValidWholesalerInvoiceNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_wholesaler_invoice_number"),
        };
      }
      return VALID;
  }

  return VALID;
};

const checkRequired = (column, value) => {
  if (isRequiredField(column) && isBlank(value)) {
    return { isValid: false, message: translator.getError("field_required", { column }) };
  }
  return VALID;
};

export const crossFieldErrors = (data) => hcpcsOrUnitOfMeasureCrossFieldErrors(data, translator);

export default isValidMedicalEncounterClaimField;
